"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import {
  activationEnergy,
  doublingInterval,
  percentPerDegree,
  q10FromRates,
  rateAtTemperature,
  rateTable,
  type TempUnit,
  temperatureForRate,
} from "@/lib/tool-kit/ecology/q10";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const Q10_TEMPERATURE_COEFFICIENT_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeQ10: "Q₁₀ from two rates",
  modeRate: "Rate at a new temperature",
  modeTemp: "Temperature for a target rate",
  unitLabel: "Temperature unit",
  unitC: "°C",
  unitK: "K",
  unitF: "°F",
  rate1Label: "Rate R₁ (any unit)",
  temp1Label: "Temperature T₁",
  rate2Label: "Rate R₂ (same unit)",
  temp2Label: "Temperature T₂",
  q10Label: "Q₁₀",
  results: "Results",
  q10Result: "Q₁₀",
  rateResult: "Rate R₂ at T₂ = {temp} {unit}",
  tempResult: "Temperature T₂ for R₂ = {rate}",
  ratio: "Rate ratio R₂ ÷ R₁",
  perDegree: "Change in rate per degree ({unit})",
  doubling: "Rise in temperature that doubles the rate ({unit})",
  doublingNone: "none (Q₁₀ is 1 or less)",
  activation: "Activation energy that fits (kJ/mol)",
  workingHeading: "The working",
  workingQ10:
    "Q₁₀ = (R₂ ÷ R₁)^(10 ÷ ΔT) = ({r2} ÷ {r1})^(10 ÷ {span}) = {result}",
  workingRate:
    "R₂ = R₁ × Q₁₀^(ΔT ÷ 10) = {r1} × {q10}^({span} ÷ 10) = {result}",
  workingTemp:
    "T₂ = T₁ + 10 × ln(R₂ ÷ R₁) ÷ ln Q₁₀ = {t1} + 10 × ln({r2} ÷ {r1}) ÷ ln({q10}) = {result} {unit}",
  workingSpan:
    "Here ΔT = T₂ − T₁ is {delta} {unit}, which is {celsius} Celsius degrees.",
  tableHeading: "Rate at other temperatures (same Q₁₀)",
  columnTemp: "Temperature ({unit})",
  columnRate: "Rate",
  columnFactor: "× the rate at T₁",
  noteLow:
    "Q₁₀ below 1 means the rate fell as the temperature rose — typically a process past its optimum, such as an enzyme starting to denature.",
  noteTypical:
    "Most biological processes have a Q₁₀ between about 2 and 3; physical processes limited by diffusion are often lower, around 1.1 to 1.5.",
  noteRange:
    "Q₁₀ is not constant over a wide temperature range, so use it only between temperatures near the ones you measured.",
  assumptions:
    "Assumes the rate changes exponentially with temperature. For a rigorous temperature law, the Arrhenius equation is the physical model; its activation energy is shown for comparison.",
  copyResult: "Copy result",
  copied: "Copied!",
  copyQ10: "Q₁₀ = {q10} (R₁ = {r1} at {t1} {unit}; R₂ = {r2} at {t2} {unit})",
  copyRate: "R₂ = {r2} at {t2} {unit} (R₁ = {r1} at {t1} {unit}, Q₁₀ = {q10})",
  copyTemp:
    "T₂ = {t2} {unit} for R₂ = {r2} (R₁ = {r1} at {t1} {unit}, Q₁₀ = {q10})",
  enter: "Fill in every number to see the result.",
  errRate: "Rates must be greater than 0.",
  errTemps: "The two temperatures must be different.",
  errQ10: "Q₁₀ must be greater than 0.",
  errNoChange:
    "With Q₁₀ = 1 the rate never changes with temperature, so no temperature gives a different rate.",
} as const;

type Strings = typeof Q10_TEMPERATURE_COEFFICIENT_CALCULATOR_STRINGS;
type Mode = "q10" | "rate" | "temp";

const ID = "qt";

/** The inputs each mode reads. */
const NEEDS: Record<Mode, (keyof Inputs)[]> = {
  q10: ["r1", "t1", "r2", "t2"],
  rate: ["r1", "t1", "t2", "q10"],
  temp: ["r1", "t1", "r2", "q10"],
};

interface Inputs {
  r1: number;
  t1: number;
  r2: number;
  t2: number;
  q10: number;
}

export default function Q10TemperatureCoefficientCalculatorWidget() {
  const strings = useToolUi(Q10_TEMPERATURE_COEFFICIENT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("q10");
  const [unit, setUnit] = useState<TempUnit>("c");
  const [text, setText] = useState({
    r1: "2.4",
    t1: "10",
    r2: "7.1",
    t2: "25",
    q10: "2.5",
  });
  const set = (key: keyof typeof text) => (value: string) =>
    setText((old) => ({ ...old, [key]: value }));
  const parsed = {
    r1: parseMeasuredNumber(text.r1),
    t1: parseMeasuredNumber(text.t1),
    r2: parseMeasuredNumber(text.r2),
    t2: parseMeasuredNumber(text.t2),
    q10: parseMeasuredNumber(text.q10),
  };
  const needs = NEEDS[mode];
  const ready = needs.every((key) => parsed[key] !== null);
  const field = (key: keyof Inputs, label: string) =>
    needs.includes(key) ? (
      <TextField
        id={`${ID}-${key}`}
        key={key}
        label={label}
        numeric
        onChange={set(key)}
        value={text[key]}
      />
    ) : null;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name={`${ID}-mode`}
        onChange={setMode}
        options={[
          { value: "q10", label: strings.modeQ10 },
          { value: "rate", label: strings.modeRate },
          { value: "temp", label: strings.modeTemp },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {field("q10", strings.q10Label)}
        {field("r1", strings.rate1Label)}
        {field("t1", strings.temp1Label)}
        {field("r2", strings.rate2Label)}
        {field("t2", strings.temp2Label)}
        <SelectField
          id={`${ID}-unit`}
          label={strings.unitLabel}
          onChange={setUnit}
          options={[
            { value: "c", label: strings.unitC },
            { value: "k", label: strings.unitK },
            { value: "f", label: strings.unitF },
          ]}
          value={unit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {ready ? (
          <Outcome
            fmt={fmt}
            inputs={parsed as unknown as Inputs}
            mode={mode}
            strings={strings}
            unit={unit}
          />
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
      </section>
    </div>
  );
}

function Outcome({
  mode,
  inputs,
  unit,
  strings,
  fmt,
}: {
  mode: Mode;
  inputs: Inputs;
  unit: TempUnit;
  strings: Strings;
  fmt: NumberFormat;
}) {
  const { r1, t1, r2, t2 } = inputs;
  let q10: number | null = inputs.q10;
  let result: number | null = null;
  if (mode === "q10") {
    q10 = q10FromRates(r1, t1, r2, t2, unit);
    result = q10;
  } else if (mode === "rate") {
    result = rateAtTemperature(r1, t1, t2, q10, unit);
  } else {
    result = temperatureForRate(r1, t1, r2, q10, unit);
  }
  if (!(r1 > 0) || (mode !== "rate" && !(r2 > 0))) {
    return <ErrorNotice>{strings.errRate}</ErrorNotice>;
  }
  if (mode === "q10" && t1 === t2) {
    return <ErrorNotice>{strings.errTemps}</ErrorNotice>;
  }
  if (mode !== "q10" && !(inputs.q10 > 0)) {
    return <ErrorNotice>{strings.errQ10}</ErrorNotice>;
  }
  if (result === null || q10 === null) {
    return <ErrorNotice>{strings.errNoChange}</ErrorNotice>;
  }
  return (
    <Shown
      fmt={fmt}
      inputs={inputs}
      mode={mode}
      q10={q10}
      result={result}
      strings={strings}
      unit={unit}
    />
  );
}

function headlineFor(
  mode: Mode,
  ctx: {
    inputs: Inputs;
    q10: number;
    result: number;
    unitText: string;
    num: (value: number) => string;
    strings: Strings;
  }
): { label: string; value: string } {
  const { inputs, q10, result, unitText, num, strings } = ctx;
  if (mode === "q10") {
    return { label: strings.q10Result, value: num(q10) };
  }
  if (mode === "rate") {
    return {
      label: t(strings.rateResult, { temp: num(inputs.t2), unit: unitText }),
      value: num(result),
    };
  }
  return {
    label: t(strings.tempResult, { rate: num(inputs.r2) }),
    value: `${num(result)} ${unitText}`,
  };
}

function Shown({
  mode,
  inputs,
  q10,
  result,
  unit,
  strings,
  fmt,
}: {
  mode: Mode;
  inputs: Inputs;
  q10: number;
  result: number;
  unit: TempUnit;
  strings: Strings;
  fmt: NumberFormat;
}) {
  const num = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const unitText = { c: strings.unitC, k: strings.unitK, f: strings.unitF }[
    unit
  ];
  const finalRate = mode === "rate" ? result : inputs.r2;
  const finalTemp = mode === "temp" ? result : inputs.t2;
  const span = finalTemp - inputs.t1;
  const celsius = unit === "f" ? (span * 5) / 9 : span;
  const doubling = doublingInterval(q10, unit);
  const energy =
    mode === "q10"
      ? activationEnergy(inputs.r1, inputs.t1, inputs.r2, inputs.t2, unit)
      : null;
  const vars = {
    r1: num(inputs.r1),
    t1: num(inputs.t1),
    r2: num(finalRate),
    t2: num(finalTemp),
    q10: num(q10),
    unit: unitText,
  };
  const rows = rateTable(inputs.r1, inputs.t1, q10, unit).map((point) => [
    num(point.temperature),
    num(point.rate),
    num(point.factor),
  ]);
  const headline = headlineFor(mode, {
    inputs,
    q10,
    result,
    unitText,
    num,
    strings,
  });
  const copy = t(
    { q10: strings.copyQ10, rate: strings.copyRate, temp: strings.copyTemp }[
      mode
    ],
    vars
  );
  return (
    <div className="space-y-6">
      <StatGrid>
        <StatCard emphasis label={headline.label} value={headline.value} />
        {mode === "q10" ? null : (
          <StatCard label={strings.q10Result} value={num(q10)} />
        )}
        <StatCard label={strings.ratio} value={num(finalRate / inputs.r1)} />
        <StatCard
          label={t(strings.perDegree, { unit: unitText })}
          value={`${num(percentPerDegree(q10, unit))}%`}
        />
        <StatCard
          label={t(strings.doubling, { unit: unitText })}
          value={doubling === null ? strings.doublingNone : num(doubling)}
        />
        {energy === null ? null : (
          <StatCard label={strings.activation} value={num(energy / 1000)} />
        )}
      </StatGrid>
      {q10 < 1 ? <Notice>{strings.noteLow}</Notice> : null}
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <ul className="space-y-1 font-mono text-foreground text-sm">
          <li>
            {mode === "q10"
              ? t(strings.workingQ10, {
                  ...vars,
                  span: num(celsius),
                  result: num(q10),
                })
              : null}
            {mode === "rate"
              ? t(strings.workingRate, {
                  ...vars,
                  span: num(celsius),
                  result: num(result),
                })
              : null}
            {mode === "temp"
              ? t(strings.workingTemp, { ...vars, result: num(result) })
              : null}
          </li>
          {mode === "temp" || unit !== "f" ? null : (
            <li>
              {t(strings.workingSpan, {
                delta: num(span),
                unit: unitText,
                celsius: num(celsius),
              })}
            </li>
          )}
        </ul>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <DataTable
          head={[
            t(strings.columnTemp, { unit: unitText }),
            strings.columnRate,
            strings.columnFactor,
          ]}
          rows={rows}
        />
      </div>
      <p className={HINT}>
        {strings.noteTypical} {strings.noteRange} {strings.assumptions}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        text={copy}
      />
    </div>
  );
}
